import React, { useState, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, DownloadIcon } from 'lucide-react';
import { FormSection, DisplayField, TextAreaField } from '../components/FormComponents';
import { StatusBadge } from '../components/StatusBadge';
import { mockPRs, mockApprovals, mockSuppliers, mockPOs } from '../data/mockData';
const mockAnnualPlanDetails: Record<string, any> = {
  'plan-2025-26': {
    id: 'plan-2025-26',
    fiscalYear: 'FY 2025/26',
    status: 'Submitted',
    totalBudget: 625000,
    totalItems: 4,
    submittedBy: 'Admin User',
    submittedDate: '2025-03-20',
    department: 'Procurement',
    remarks: 'Annual plan for FY 2025/26 covering IT, facilities, and services.',
    items: [{
      id: '1',
      type: 'Goods',
      description: 'Laptop Pro X',
      qty: 20,
      unitPrice: 1200,
      total: 24000,
      priority: 'High',
      quarter: 'Q1'
    }, {
      id: '2',
      type: 'Goods',
      description: 'A4 Printer Paper',
      qty: 100,
      unitPrice: 25,
      total: 2500,
      priority: 'Low',
      quarter: 'Q1'
    }, {
      id: '3',
      type: 'Works',
      description: 'Warehouse Expansion Phase 2',
      qty: 1,
      unitPrice: 350000,
      total: 350000,
      priority: 'High',
      quarter: 'Q2'
    }, {
      id: '4',
      type: 'Service',
      description: 'Annual IT Support Contract',
      qty: 1,
      unitPrice: 60000,
      total: 60000,
      priority: 'Medium',
      quarter: 'Q1'
    }]
  },
  'plan-2024-25-v2': {
    id: 'plan-2024-25-v2',
    fiscalYear: 'FY 2024/25 (Revised)',
    status: 'Submitted',
    totalBudget: 710000,
    totalItems: 5,
    submittedBy: 'John Doe',
    submittedDate: '2024-11-05',
    department: 'Procurement',
    remarks: 'Revised plan with additional security and fleet items.',
    items: [{
      id: '1',
      type: 'Goods',
      description: 'Server Rack Units',
      qty: 5,
      unitPrice: 8000,
      total: 40000,
      priority: 'High',
      quarter: 'Q1'
    }, {
      id: '2',
      type: 'Goods',
      description: 'Office Chairs Ergonomic',
      qty: 50,
      unitPrice: 800,
      total: 40000,
      priority: 'Medium',
      quarter: 'Q2'
    }, {
      id: '3',
      type: 'Works',
      description: 'Office Renovation Block B',
      qty: 1,
      unitPrice: 200000,
      total: 200000,
      priority: 'High',
      quarter: 'Q2'
    }, {
      id: '4',
      type: 'Service',
      description: 'Security Services Annual',
      qty: 1,
      unitPrice: 120000,
      total: 120000,
      priority: 'High',
      quarter: 'Q1'
    }, {
      id: '5',
      type: 'Service',
      description: 'Fleet Management Contract',
      qty: 1,
      unitPrice: 150000,
      total: 150000,
      priority: 'Medium',
      quarter: 'Q3'
    }]
  }
};
interface GenericApprovalActionProps {
  type: 'supplier' | 'pr' | 'eoi' | 'rfp' | 'rfq' | 'noa' | 'po' | 'requisition' | 'annual-plan';
}
export function GenericApprovalAction({
  type
}: GenericApprovalActionProps) {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [remarks, setRemarks] = useState('');
  // Fetch mock data based on type
  let data: any = null;
  let title = '';
  if (type === 'supplier') {
    data = mockSuppliers.find(s => s.id === id);
    title = 'Supplier Approval';
  } else if (type === 'pr') {
    data = mockPRs.find(p => p.id === id);
    title = 'Purchase Requisition Approval';
  } else if (type === 'po') {
    const poData = mockPOs.find(p => p.id === id);
    if (poData) {
      // Attach mock delivery phases
      const mockPhases: Record<string, any[]> = {
        '1': [{
          description: 'Initial delivery',
          deliveryDate: '2023-11-01',
          paymentPercent: 60,
          items: [{
            product: 'Laptop Pro X',
            qty: 3
          }]
        }, {
          description: 'Final delivery',
          deliveryDate: '2023-11-15',
          paymentPercent: 40,
          items: [{
            product: 'Laptop Pro X',
            qty: 2
          }]
        }],
        '2': [{
          description: 'Paper delivery',
          deliveryDate: '2023-12-01',
          paymentPercent: 50,
          items: [{
            product: 'A4 Printer Paper',
            qty: 100
          }]
        }, {
          description: 'Cartridge delivery',
          deliveryDate: '2024-01-10',
          paymentPercent: 50,
          items: [{
            product: 'Ink Cartridges',
            qty: 50
          }, {
            product: 'A4 Printer Paper',
            qty: 100
          }]
        }],
        '3': [{
          description: 'Phase 1 - First unit',
          deliveryDate: '2023-11-15',
          paymentPercent: 50,
          items: [{
            product: 'Industrial Generator 50kW',
            qty: 1
          }]
        }, {
          description: 'Phase 2 - Second unit',
          deliveryDate: '2023-11-30',
          paymentPercent: 50,
          items: [{
            product: 'Industrial Generator 50kW',
            qty: 1
          }]
        }]
      };
      data = {
        ...poData,
        deliveryPhases: mockPhases[poData.id] || []
      };
    }
    title = 'Purchase Order Approval';
  } else if (type === 'annual-plan') {
    data = id ? mockAnnualPlanDetails[id] : null;
    title = 'Annual Plan Approval';
  } else {
    data = mockApprovals.find(a => a.id === id);
    title = `${type.toUpperCase()} Approval`;
  }
  if (!data) return <div data-id="element-867">Record not found</div>;
  const handleAction = (action: 'approve' | 'reject') => {
    if (action === 'reject' && !remarks) {
      alert('Remarks are mandatory for rejection.');
      return;
    }
    alert(`Successfully ${action}d!`);
    navigate(-1);
  };
  return <div className="space-y-6 max-w-5xl mx-auto" data-id="element-868">
      <div className="flex items-center justify-between" data-id="element-869">
        <div data-id="element-870">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-871">{title}</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-872">
            Review details and take action.
          </p>
        </div>
        <StatusBadge status={data.status} className="text-sm px-3 py-1" data-id="element-873" />
      </div>

      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8 space-y-8" data-id="element-874">
        {/* Dynamic Details Rendering based on Type */}
        {type === 'supplier' && <>
            <FormSection title="Supplier Details" data-id="element-875">
              <DisplayField label="Supplier Code" value={data.code} data-id="element-876" />
              <DisplayField label="Supplier Name" value={data.name} data-id="element-877" />
              <DisplayField label="Category" value={data.category} data-id="element-878" />
              <DisplayField label="Country" value={data.countryOfOrigin} data-id="element-879" />
              <DisplayField label="Contact Person" value={data.contactPersonName} data-id="element-880" />
              <DisplayField label="Email" value={data.email} data-id="element-881" />
            </FormSection>
            <FormSection title="Bank Details" data-id="element-882">
              <DisplayField label="Bank Name" value={data.bankName} data-id="element-883" />
              <DisplayField label="Account Number" value={data.accountNumber} data-id="element-884" />
            </FormSection>
          </>}

        {type === 'pr' && <>
            <FormSection title="Requisition Details" data-id="element-885">
              <DisplayField label="PR No" value={data.prNo} data-id="element-886" />
              <DisplayField label="Originator" value={data.originator} data-id="element-887" />
              <DisplayField label="Department" value={data.departmentCode} data-id="element-888" />
              <DisplayField label="Procurement Method" value={data.procurementMethod} data-id="element-889" />
              <DisplayField label="Total Amount" value={`${data.total.toLocaleString()}`} data-id="element-890" />
            </FormSection>

            <div className="mb-8" data-id="element-891">
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-892">
                Line Items
              </h3>
              <div className="overflow-x-auto" data-id="element-893">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-894">
                  <thead className="bg-gray-50" data-id="element-895">
                    <tr data-id="element-896">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-897">
                        Product
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-898">
                        Qty
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-899">
                        Rate
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-900">
                        Est. Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-901">
                    {data.lineItems.map((item: any) => <tr key={item.id} data-id="element-902">
                        <td className="px-4 py-2 text-sm text-gray-900" data-id="element-903">
                          {item.product}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500" data-id="element-904">
                          {item.qty}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500" data-id="element-905">
                          ${item.rate}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 font-medium" data-id="element-906">
                          ${item.estimatedCost}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </>}

        {type === 'po' && <>
            <FormSection title="Purchase Order Details" data-id="element-907">
              <DisplayField label="PO No" value={data.poNo} data-id="element-908" />
              <DisplayField label="Linked PR" value={data.linkedPRNo} data-id="element-909" />
              <DisplayField label="Vendor" value={data.vendor} data-id="element-910" />
              <DisplayField label="Department" value={data.department} data-id="element-911" />
              <DisplayField label="Project" value={data.project} data-id="element-912" />
              <DisplayField label="Procurement Method" value={data.procurementMethod} data-id="element-913" />
              <DisplayField label="PO Date" value={data.poDate} data-id="element-914" />
              <DisplayField label="Delivery Date" value={data.deliveryDate} data-id="element-915" />
              <DisplayField label="Total Amount" value={`${data.totalAmount.toLocaleString()}`} data-id="element-916" />
              <DisplayField label="Submitted By" value={data.submittedBy || '—'} data-id="element-917" />
            </FormSection>

            <div className="mb-8" data-id="element-918">
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-919">
                Line Items
              </h3>
              <div className="overflow-x-auto" data-id="element-920">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-921">
                  <thead className="bg-gray-50" data-id="element-922">
                    <tr data-id="element-923">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-924">
                        Product
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-925">
                        Qty
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-926">
                        Rate
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-927">
                        Est. Cost
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-928">
                        Specification
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-929">
                    {data.lineItems?.map((item: any) => <tr key={item.id} data-id="element-930">
                        <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-931">
                          {item.product}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 text-right" data-id="element-932">
                          {item.qty}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 text-right" data-id="element-933">
                          ${item.rate.toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right" data-id="element-934">
                          ${item.estimatedCost.toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500" data-id="element-935">
                          {item.specification || '—'}
                        </td>
                      </tr>)}
                  </tbody>
                  <tfoot className="bg-gray-50" data-id="element-936">
                    <tr data-id="element-937">
                      <td colSpan={3} className="px-4 py-2 text-sm font-semibold text-gray-900 text-right" data-id="element-938">
                        Total
                      </td>
                      <td className="px-4 py-2 text-sm font-bold text-indigo-600 text-right" data-id="element-939">
                        ${data.totalAmount.toLocaleString()}
                      </td>
                      <td data-id="element-940"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Phase-wise Delivery Schedule */}
            {data.deliveryPhases && data.deliveryPhases.length > 0 && <div className="mb-8" data-id="element-941">
                <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-942">
                  Phase-wise Delivery Schedule
                </h3>
                <div className="space-y-3" data-id="element-943">
                  {data.deliveryPhases.map((phase: any, idx: number) => <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-944">
                      <div className="bg-gray-50 px-4 py-3 flex items-center gap-4" data-id="element-945">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold flex-shrink-0" data-id="element-946">
                          {idx + 1}
                        </span>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" data-id="element-947">
                          <div data-id="element-948">
                            <span className="text-gray-500" data-id="element-949">Description: </span>
                            <span className="font-medium text-gray-900" data-id="element-950">
                              {phase.description || '—'}
                            </span>
                          </div>
                          <div data-id="element-951">
                            <span className="text-gray-500" data-id="element-952">
                              Delivery Date:{' '}
                            </span>
                            <span className="font-medium text-gray-900" data-id="element-953">
                              {phase.deliveryDate || '—'}
                            </span>
                          </div>
                          <div data-id="element-954">
                            <span className="text-gray-500" data-id="element-955">Payment: </span>
                            <span className="font-medium text-indigo-600" data-id="element-956">
                              {phase.paymentPercent}%
                            </span>
                          </div>
                        </div>
                      </div>
                      {phase.items && phase.items.length > 0 && <div className="px-4 py-2 border-t border-gray-200 bg-white" data-id="element-957">
                          <table className="min-w-full divide-y divide-gray-100" data-id="element-958">
                            <thead data-id="element-959">
                              <tr data-id="element-960">
                                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-961">
                                  Item
                                </th>
                                <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase w-24" data-id="element-962">
                                  Qty
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100" data-id="element-963">
                              {phase.items.map((pi: any, piIdx: number) => <tr key={piIdx} data-id="element-964">
                                  <td className="px-3 py-1.5 text-sm text-gray-900" data-id="element-965">
                                    {pi.product}
                                  </td>
                                  <td className="px-3 py-1.5 text-sm text-gray-900 text-right font-medium" data-id="element-966">
                                    {pi.qty}
                                  </td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>}
                    </div>)}
                </div>
              </div>}
          </>}

        {type === 'annual-plan' && <>
            <FormSection title="Plan Overview" data-id="element-967">
              <DisplayField label="Fiscal Year" value={data.fiscalYear} data-id="element-968" />
              <DisplayField label="Submitted By" value={data.submittedBy} data-id="element-969" />
              <DisplayField label="Submitted Date" value={data.submittedDate} data-id="element-970" />
              <DisplayField label="Department" value={data.department} data-id="element-971" />
              <DisplayField label="Total Amount" value={`$${data.totalBudget.toLocaleString()}`} data-id="element-972" />
              <DisplayField label="Total Items" value={data.totalItems} data-id="element-973" />
            </FormSection>

            {data.remarks && <FormSection title="Plan Remarks" data-id="element-974">
                <div className="md:col-span-2" data-id="element-975">
                  <p className="text-sm text-gray-700" data-id="element-976">{data.remarks}</p>
                </div>
              </FormSection>}

            <div className="mb-8" data-id="element-977">
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-978">
                Plan Line Items
              </h3>
              <div className="overflow-x-auto" data-id="element-979">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-980">
                  <thead className="bg-gray-50" data-id="element-981">
                    <tr data-id="element-982">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-983">
                        Type
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-984">
                        Description
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-985">
                        Qty
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-986">
                        Unit Price
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-987">
                        Total
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-988">
                        Priority
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-989">
                    {data.items.map((item: any) => {
                  const typeColor = item.type === 'Goods' ? 'bg-blue-100 text-blue-800' : item.type === 'Works' ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800';
                  const priorityColor = item.priority === 'High' ? 'text-red-600' : item.priority === 'Medium' ? 'text-amber-600' : 'text-green-600';
                  return <tr key={item.id} data-id="element-990">
                          <td className="px-4 py-2" data-id="element-991">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeColor}`} data-id="element-992">
                              {item.type}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900" data-id="element-993">
                            {item.description}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 text-right" data-id="element-994">
                            {item.qty}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 text-right" data-id="element-995">
                            ${item.unitPrice.toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 font-medium text-right" data-id="element-996">
                            ${item.total.toLocaleString()}
                          </td>
                          <td className="px-4 py-2" data-id="element-997">
                            <span className={`text-sm font-medium ${priorityColor}`} data-id="element-998">
                              {item.priority}
                            </span>
                          </td>
                        </tr>;
                })}
                  </tbody>
                  <tfoot className="bg-gray-50" data-id="element-999">
                    <tr data-id="element-1000">
                      <td colSpan={4} className="px-4 py-2 text-sm font-semibold text-gray-900 text-right" data-id="element-1001">
                        Grand Total
                      </td>
                      <td className="px-4 py-2 text-sm font-bold text-gray-900 text-right" data-id="element-1002">
                        $
                        {data.items.reduce((sum: number, item: any) => sum + item.total, 0).toLocaleString()}
                      </td>
                      <td data-id="element-1003"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>}

        {['eoi', 'rfp', 'rfq', 'noa'].includes(type) && <FormSection title="Document Details" data-id="element-1004">
            <DisplayField label="Reference No" value={data.refNo} data-id="element-1005" />
            <DisplayField label="Procurement Title" value={data.title} data-id="element-1006" />
            <DisplayField label="Method" value={data.method} data-id="element-1007" />
            <DisplayField label="Submitted By" value={data.submittedBy} data-id="element-1008" />
            {data.supplier && <DisplayField label="Selected Supplier" value={data.supplier} data-id="element-1009" />}
            {data.amount && <DisplayField label="Amount" value={`$${data.amount.toLocaleString()}`} data-id="element-1010" />}
          </FormSection>}

        <FormSection title="Attachments" data-id="element-1011">
          <div className="md:col-span-2" data-id="element-1012">
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1013">
              <DownloadIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-1014" />
              Download Supporting Documents.zip
            </button>
          </div>
        </FormSection>

        <div className="border-t border-gray-200 pt-8" data-id="element-1015">
          <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1016">
            Approval Action
          </h3>
          <TextAreaField label="Approver Remarks" name="remarks" value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Enter remarks (mandatory for rejection)..." rows={3} data-id="element-1017" />

          <div className="mt-6 flex space-x-4" data-id="element-1018">
            <button onClick={() => handleAction('approve')} className="flex-1 inline-flex justify-center items-center px-4 py-3 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700" data-id="element-1019">
              <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-1020" /> Approve
            </button>
            <button onClick={() => handleAction('reject')} className="flex-1 inline-flex justify-center items-center px-4 py-3 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700" data-id="element-1021">
              <XCircleIcon className="h-5 w-5 mr-2" data-id="element-1022" /> Reject
            </button>
          </div>
        </div>
      </div>
    </div>;
}